import React, { useCallback, useEffect, useState } from "react";
import "./index.scss";
import SelectComponent from "../select";
import { Formik, Form, ErrorMessage } from "formik";
import { getCounties, getStates, getZipCodes } from "containers/policies/api";
import { initialMetaForTable } from "constants/common";
import {
  useDebouncedCountiesDataCallback,
  useDebouncedStatesDataCallback,
  useDebouncedZipCodesDataCallback,
} from "utils/debouncedHelper";
import AsyncSelect from "components/common/select/AsyncSelect";
import {
  ageRequirementOptions,
  applicationFeeOptions,
  creditScoreOptions,
  securityDepositOptions,
} from "components/housing-database/addhousingdata/HousingDatabaseModal";
import { capitalizeName } from "utils/common";
const RangeSlider = ({ label, min, max, value, onChange, name }) => {
  const position = ((value - min) / (max - min)) * 100;

  return (
    <div className="mb-3">
      {label && <div className="text-black heading-xsb mb-2">{label}</div>}
      <div className="position-relative w-100 range-wrapper mt-5">
        <input
          type="range"
          min={min}
          max={max}
          value={value}
          onChange={(e) => onChange(name, Number(e.target.value))}
          className="form-range w-100"
        />
        <div className="range-fill" style={{ width: `${position}%` }} />
        <div className="range-marker" style={{ left: `${position + 2}%` }}>
          <p className="opacity-75 text-black fw-medium mb-1">{value} miles</p>
        </div>
      </div>
    </div>
  );
};

const CheckboxToYesNo = ({
  label,
  name,
  values,
  setFieldValue,
  isClearable,
}) => {
  const yesNoOptions = [
    { label: "Yes", value: true },
    { label: "No", value: false },
  ];

  return (
    <div className="mb-2">
      <SelectComponent
        label={label}
        name={name}
        options={yesNoOptions}
        selectedValue={values[name]}
        placeholder="Select Yes/No"
        isClearable={isClearable}
        handleChange={(obj) => {
          setFieldValue(name, obj ? obj.value : null);
        }}
      />
    </div>
  );
};

const YesNoWithMultiSelect = ({
  label,
  name,
  values,
  setFieldValue,
  options,
  isClearable = true,
}) => {
  const yesNo = [
    { label: "Yes", value: true },
    { label: "No", value: false },
  ];

  return (
    <div className="mb-2">
      <SelectComponent
        label={label}
        name={`${name}.checked`}
        options={yesNo}
        selectedValue={values[name]?.checked}
        placeholder="Select Yes/No"
        isClearable={isClearable}
        handleChange={(obj) =>
          setFieldValue(`${name}.checked`, obj ? obj.value : null)
        }
      />

      {values[name]?.checked && (
        <SelectComponent
          className="mt-2"
          name={`${name}.subItem`}
          label={`${label} Options`}
          isMulti
          options={options}
          selectedValue={values[name]?.subItem || []}
          placeholder="Select Options"
          handleChange={(arr) => setFieldValue(`${name}.subItem`, arr)}
        />
      )}
    </div>
  );
};

const FilterModal = ({
  isModalOpen,
  setIsModalOpen,
  currentFilters,
  handleSubmission,
}) => {
  const [countiesList, setCountiesList] = useState([]);
  const [zipCodes, setZipCodes] = useState([]);
  const [statesList, setStatesList] = useState([]);
  const debouncedCoutiesDataCallback = useDebouncedCountiesDataCallback();
  const debouncedStatesDataCallback = useDebouncedStatesDataCallback();
  const debounceZipCodes = useDebouncedZipCodesDataCallback();

  const fetchZipCodes = async function(county_id) {
    try {
      const result = await getZipCodes({
        ...initialMetaForTable,
        county_id: county_id,
      });
      if (result["zip_codes"]) {
        const data = result?.zip_codes;
        setZipCodes(data);
      }
    } catch (error) {
      console.log(error);
    }
  };
  // useEffect(() => {
  //   fetchZipCodes();
  // }, []);
  const fetchCounties = useCallback(async (stateOption) => {
    if (!stateOption?.value) {
      setCountiesList([]);
      return;
    }

    try {
      const result = await getCounties({
        ...initialMetaForTable,
        state_id: stateOption.value,
      });

      const options =
        result?.counties?.map((ct) => ({
          value: ct.id,
          label: capitalizeName(ct.name),
        })) || [];

      setCountiesList(options);
    } catch (error) {
      console.error(error);
    }
  }, []);
  useEffect(() => {
    if (currentFilters?.state?.value) {
      fetchCounties(currentFilters.state);
    }

    if (currentFilters?.county?.value) {
      fetchZipCodes(currentFilters.county.value);
    }
  }, []);

  useEffect(() => {
    if (currentFilters?.county) {
      fetchZipCodes(currentFilters?.county?.value);
    }
  }, [currentFilters]);

  const initialValues = {
    state: currentFilters?.state ?? null,
    county: currentFilters?.county ?? null,
    zipcode: currentFilters?.zipcode ?? null,
    distance: currentFilters?.distance ?? 15,
    rent: currentFilters?.rent ?? [],
    income: currentFilters?.income ?? [],

    creditScore: currentFilters?.creditScore ?? { checked: null, subItem: [] },
    ageRequirement: currentFilters?.ageRequirement ?? {
      checked: null,
      subItem: [],
    },
    securityDeposit: currentFilters?.securityDeposit ?? {
      checked: null,
      subItem: [],
    },
    application_fee: currentFilters?.application_fee ?? {
      checked: null,
      subItem: [],
    },

    noEviction: currentFilters?.noEviction ?? null,
    willingToTakeEviction: currentFilters?.willingToTakeEviction ?? null,
    creditCheck: currentFilters?.creditCheck ?? null,
    background: currentFilters?.background ?? null,
    rent_history: currentFilters?.rent_history ?? null,
    party_checks: currentFilters?.party_checks ?? null,
    no_bankrupty: currentFilters?.no_bankrupty ?? null,
  };
  const incomeRanges = [
    { value: "0-30000", label: "$0 - $30,000" },
    { value: "30001-58020", label: "$30,001 - $58,020" },
    { value: "58021-94000", label: "$58,021 - $94,000" },
    { value: "94001-153000", label: "$94,001 - $153,000" },
  ];

  const rentRanges = [
    { value: "0-1749", label: "$0 - $1,749" },
    { value: "1750-1949", label: "$1,750 - $1,949" },
    { value: "1950-2349", label: "$1,950 - $2,349" },
    { value: "2350-3149", label: "$2,350 - $3,149" },
    { value: "3150-3675", label: "$3,150 - $3,675" },
    { value: "3676-4000", label: "$3,676 - $4,000" },
  ];

  const fetchStates = useCallback(async () => {
    try {
      const result = await getStates({ ...initialMetaForTable });

      const options =
        result?.states?.map((st) => ({
          value: st.id,
          label: st.name.toUpperCase(),
        })) || [];

      options.sort((a, b) => a.label.localeCompare(b.label));

      setStatesList(options);
    } catch (error) {
      console.error("Error fetching states:", error);
    }
  }, []);
  useEffect(() => {
    fetchStates();
  }, [fetchStates]);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values) => {
        const payload = {
          ...values,
        };
        handleSubmission(payload);
        setIsModalOpen(false);
      }}
    >
      {({ values, handleSubmit, setFieldValue, errors, isSubmitting }) => {
        return (
          <Form onSubmit={handleSubmit}>
            <div className="input-flex row">
              <div className="col-sm-12">
                <AsyncSelect
                  label="State"
                  name="state"
                  isClearable
                  options={statesList}
                  loadOptions={(inputValue) =>
                    new Promise((resolve) => {
                      debouncedStatesDataCallback(inputValue, resolve);
                    })
                  }
                  selectedValue={values.state}
                  placeholder="Select State"
                  handleChange={(option) => {
                    fetchCounties(option);
                    setFieldValue("state", option);
                    setFieldValue("county", null);
                    setFieldValue("zipcode", null);
                  }}
                />
              </div>
            </div>

            <div className="input-flex row">
              <div className="col-sm-12">
                <AsyncSelect
                  isClearable
                  label="County"
                  name="county"
                  options={countiesList}
                  loadOptions={(inputValue) =>
                    new Promise((resolve) => {
                      debouncedCoutiesDataCallback(
                        inputValue,
                        values.state?.value,
                        resolve,
                      );
                    })
                  }
                  selectedValue={values.county}
                  placeholder="Select County"
                  handleChange={(option) => {
                    setFieldValue("county", option);
                    setFieldValue("zipcode", null);
                    if (option) fetchZipCodes(option.value);
                  }}
                  disabled={!values.state}
                />
              </div>
              <div className="col-sm-12">
                <AsyncSelect
                  label="Zip Code"
                  options={zipCodes.map((item) => ({
                    label: item?.name,
                    value: item?.id,
                  }))}
                  disabled={!values?.county}
                  selectedValue={values.zipcode || null}
                  placeholder="Select Zip Code"
                  isClearable={true}
                  handleChange={(obj) => setFieldValue("zipcode", obj || null)}
                  loadOptions={(inputValue) =>
                    new Promise((resolve) => {
                      debounceZipCodes(
                        inputValue,
                        "",
                        values?.county?.value,
                        "",
                        resolve,
                      );
                    })
                  }
                />
              </div>

              {values.county && values.zipcode && (
                <RangeSlider
                  label="Distance"
                  min={1}
                  max={100}
                  value={values.distance}
                  onChange={setFieldValue}
                  name="distance"
                />
              )}
              <div className="col-sm-12">
                <SelectComponent
                  name="rent"
                  label="Rent"
                  options={rentRanges}
                  selectedValue={values.rent}
                  isMulti
                  placeholder="Select Rent..."
                  handleChange={(arr) => {
                    setFieldValue("rent", arr);
                  }}
                />
              </div>
              <div className="col-sm-12">
                <SelectComponent
                  name="income"
                  label="Income"
                  options={incomeRanges}
                  selectedValue={values.income}
                  isMulti
                  placeholder="Select Income..."
                  handleChange={(arr) => {
                    setFieldValue("income", arr);
                  }}
                />
              </div>

              <div className="mb-3">
                <p className="text-black heading-xsb mb-2 mt-2">
                  Resident Selection Criteria
                </p>
                <div className="row">
                  <CheckboxToYesNo
                    label="No Eviction"
                    isClearable={true}
                    name="noEviction"
                    values={values}
                    setFieldValue={setFieldValue}
                  />

                  {/* <CheckboxToYesNo
                    label="Willing to Take Eviction"
                    name="willingToTakeEviction"
                    values={values}
                    setFieldValue={setFieldValue}
                  /> */}

                  <YesNoWithMultiSelect
                    label="Credit Score"
                    isClearable={true}
                    name="creditScore"
                    values={values}
                    setFieldValue={setFieldValue}
                    options={creditScoreOptions}
                  />

                  <CheckboxToYesNo
                    label="Credit Check"
                    name="creditCheck"
                    isClearable={true}
                    values={values}
                    setFieldValue={setFieldValue}
                  />

                  <YesNoWithMultiSelect
                    label="Age Requirement"
                    name="ageRequirement"
                    values={values}
                    isClearable={true}
                    setFieldValue={setFieldValue}
                    options={ageRequirementOptions}
                  />

                  <YesNoWithMultiSelect
                    label="Security Deposit"
                    name="securityDeposit"
                    values={values}
                    isClearable={true}
                    setFieldValue={setFieldValue}
                    options={securityDepositOptions}
                  />

                  <YesNoWithMultiSelect
                    label="Application Fee"
                    name="application_fee"
                    isClearable={true}
                    values={values}
                    setFieldValue={setFieldValue}
                    options={applicationFeeOptions}
                  />

                  <CheckboxToYesNo
                    label="Background"
                    name="background"
                    values={values}
                    setFieldValue={setFieldValue}
                    isClearable={true}
                  />

                  <CheckboxToYesNo
                    label="Rental History"
                    name="rent_history"
                    values={values}
                    setFieldValue={setFieldValue}
                    isClearable={true}
                  />

                  <CheckboxToYesNo
                    label="No 3rd Party Checks"
                    name="party_checks"
                    values={values}
                    setFieldValue={setFieldValue}
                    isClearable={true}
                  />

                  <CheckboxToYesNo
                    label="No Bankruptcy"
                    name="no_bankrupty"
                    values={values}
                    setFieldValue={setFieldValue}
                    isClearable={true}
                  />
                </div>
              </div>
              <div className="w-100 d-flex gap-2 justify-content-between">
                {isSubmitting ? (
                  <span className="primary-btn w-50">
                    <span
                      className="spinner-grow spinner-grow-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Applying...
                  </span>
                ) : (
                  <input
                    className="primary-btn w-50"
                    type="submit"
                    name="submit"
                    value="Apply"
                  />
                )}
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="secondary-btn w-50"
                  type="button"
                >
                  <span className="text-black">Cancel</span>
                </button>
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default FilterModal;
